import { AclVerb } from '@agilelab/plugin-wb-access-control-common';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { notificationCenterNavigatedTopic } from '@agilelab/plugin-wb-events-common';
import {
  AccessControlRequestNotification,
  AccessControlResponseNotification,
  InformativeNotification,
  Notification,
  QuestionNotification,
} from '@agilelab/plugin-wb-notification-common';
import {
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbCardContent,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { NotificationType } from '@agilelab/plugin-wb-platform-common';
import { Content, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import DraftsIcon from '@material-ui/icons/Drafts';
import NotificationIcon from '@material-ui/icons/NotificationsNone';
import { Alert, Pagination } from '@material-ui/lab';
import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { AccessControlRequestNotificationDisclosure } from './AccessControlRequestNotification';
import { AccessControlResponseNotificationDisclosure } from './AccessControlResponseNotification';
import { InformativeNotificationDisclosure } from './InformativeNotification';
import { NotificationsFilters } from './NotificationsFilters';
import { QuestionNotificationDisclosure } from './QuestionNotification';
import {
  ITEMS_PER_PAGE,
  NotificationDataType,
  useNotification,
} from './useNotificationProvider';
import { WbPage } from '@agilelab/plugin-wb-platform';

function renderNoNotifications(classes: any) {
  return (
    <Grid
      container
      direction="column"
      className={classes.noNotificationsContainer}
    >
      <Grid item>
        <NotificationIcon
          fontSize="inherit"
          className={classes.noNotificationsIcon}
        />
      </Grid>
      <Grid item>
        <Typography className={classes.noNotifications}>
          No notifications
        </Typography>
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles(theme => ({
  noNotifications: {
    fontSize: theme.typography.fontSize * 1.5,
    color: theme.palette.secondary.main,
  },
  noNotificationsIcon: {
    fontSize: theme.typography.fontSize * 4,
  },
  noNotificationsContainer: {
    alignItems: 'center',
  },
  divider: {
    position: 'relative',
    '& hr': {
      borderTop: `1px solid ${theme.palette.primary.light}`,
    },
    '& span': {
      color: theme.palette.primary.main,
      position: 'absolute',
      top: '-11px',
      left: '0',
      backgroundColor: theme.palette.background.paper,
      paddingRight: theme.spacing(2),
    },
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
}));

export const NotificationsPageContent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [oldData, setOldData] = useState<NotificationDataType>(undefined);
  const [expandedNotification, setExpandedNotification] = useState<
    string | boolean
  >(false);

  const {
    page,
    setPage,
    notificationData,
    refetchNotificationData,
    loadingNotificationData,
    errorNotificationData,
    kind,
    readStatus,
    requestStatus,
  } = useNotification();

  useEffect(() => {
    refetchNotificationData();
  });

  const handlePaginationChange = (
    _event: ChangeEvent<unknown>,
    paginationValue: number,
  ) => {
    setPage(paginationValue);
  };

  const handleNotificationDisclosureChange = (
    newExpanded: string | boolean,
  ) => {
    setExpandedNotification(newExpanded);
  };

  const notificationRender = (n: Notification) => {
    switch (n.kind) {
      case NotificationType.QUESTION:
        return (
          <QuestionNotificationDisclosure
            key={n.id}
            notification={n as QuestionNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      case NotificationType.INFORMATIVE:
        return (
          <InformativeNotificationDisclosure
            key={n.id}
            notification={n as InformativeNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      case NotificationType.ACCESS_REQUEST:
        return (
          <AccessControlRequestNotificationDisclosure
            verb={AclVerb.Grant}
            key={n.id}
            notification={n as AccessControlRequestNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      case NotificationType.ACCESS_RESPONSE:
        return (
          <AccessControlResponseNotificationDisclosure
            verb={AclVerb.Grant}
            key={n.id}
            notification={n as AccessControlResponseNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      case NotificationType.REVOKE_REQUEST:
        return (
          <AccessControlRequestNotificationDisclosure
            verb={AclVerb.Revoke}
            key={n.id}
            notification={n as AccessControlRequestNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      case NotificationType.REVOKE_RESPONSE:
        return (
          <AccessControlResponseNotificationDisclosure
            verb={AclVerb.Revoke}
            key={n.id}
            notification={n as AccessControlResponseNotification}
            expandedAccordion={expandedNotification}
            onAccordionExpansionsChange={handleNotificationDisclosureChange}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!!notificationData) {
      setOldData(notificationData);
    }
  }, [notificationData]);

  useEffect(() => {
    setExpandedNotification(false);
  }, [kind, readStatus, requestStatus]);

  if (!loadingNotificationData && !notificationData && !errorNotificationData) {
    return (
      <Box style={{ marginTop: theme.spacing(6) }}>
        {renderNoNotifications(classes)}
      </Box>
    );
  }

  return (
    <>
      {loadingNotificationData && <Progress />}
      {errorNotificationData instanceof Error && (
        <Alert severity="error">{errorNotificationData.message}</Alert>
      )}
      {oldData && (
        <>
          <Box style={{ marginTop: theme.spacing(2) }}>
            {oldData.notifications.length === 0 &&
              renderNoNotifications(classes)}

            {oldData.notifications.map(notificationRender)}
            {!!oldData.notifications.length && (
              <div className={classes.paginationWrapper}>
                <Pagination
                  count={Math.ceil(oldData.total / ITEMS_PER_PAGE)}
                  showFirstButton
                  showLastButton
                  page={page}
                  onChange={handlePaginationChange}
                  size="large"
                  color="primary"
                />
              </div>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export const NotificationsPage = () => {
  const { setAllAsRead } = useNotification();
  const { publish } = usePublishEvent({ generatorSystem: 'settings' });
  const location = useLocation();

  useEffect(() => {
    publish(notificationCenterNavigatedTopic, {
      path: location.pathname,
    });
  }, [publish, location.pathname]);

  return (
    <WbPage themeId="home">
      <WbHeader
        title="Notification Center"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Notification Center</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <Content>
        <WbCard
          title="Notifications"
          actions={
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<DraftsIcon />}
              onClick={() => setAllAsRead()}
            >
              Mark all as read
            </Button>
          }
        >
          <WbCardContent>
            <NotificationsFilters />
            <NotificationsPageContent key={location.pathname} />
          </WbCardContent>
        </WbCard>
      </Content>
    </WbPage>
  );
};
